import classNames from 'classnames';
import React from 'react';

interface RCCardProps {
  /**
   * Common prebuilt variations:
   * - Muted: The default card style. on-primary => elevated on hover.
   * - Transparent: Listing card style. transparent => elevated on hover. The card background overflows the card boundaries on hover.
   * - Custom: Allows for custom background colors for default and hovered states using `customBg*` props.
   **/
  variant?: 'muted' | 'transparent' | 'custom';
  /** When using the `custom` variant, pass a token name here to define the background color token. */
  customBgColorToken?: string;
  /** When using the `custom` variant, pass a token name here to define the hover color token. */
  customBgColorHoverToken?: string;
  /** Sets `height: 100%`. Use this if you're rendering a set of `RCCard`s in a flex/grid row to ensure they all stay the same height relative to one another. */
  fullHeight?: boolean;
}

/**
 * RCCard is a lightweight wrapper that allows for easy implementation of a clickable card-shaped wrapper UI.
 *
 * Note that because of the way we use `CoreLink`, you need to use `RCCard.LinkWrapper` as a direct child to make the component properly linked & styled (see example source code, which uses a simple `<a>` tag but provides the correct structure).
 */
export function RCCard({
  fullHeight = false,
  variant = 'muted',
  customBgColorToken = undefined,
  customBgColorHoverToken = undefined,
  children = undefined,
}: React.PropsWithChildren<RCCardProps>) {
  const customColors = variant === 'custom' ? {
    '--bg-color': `var(--${customBgColorToken})`,
    '--bg-color-hover': `var(--${customBgColorHoverToken})`,
  } : {};

  const cardStyles = customColors as React.CSSProperties;

  return (
    <div
      className={classNames(
        'rc-card',
        { 'rc-card--full-height': fullHeight },
        { 'rc-card--muted': variant === 'muted' },
        { 'rc-card--transparent': variant === 'transparent' },

      )}
      style={cardStyles}
    >
      <div className="rc-card__content">
        {children}
      </div>
    </div>
  );
}

interface RCCardLinkProps {
  children: React.ReactElement<HTMLAnchorElement>;
}

export function LinkWrapper({ children }: RCCardLinkProps) {
  return React.cloneElement(children, {
    className: ['rc-card__link', children.props.className].join(' '),
  });
}

RCCard.LinkWrapper = LinkWrapper;
